<template>
  <div
    class="group col-span-1 flex flex-col justify-stretch rounded-lg border border-subtle bg-surface px-4 pb-4 transition-all"
    :class="{ 'cursor-pointer hover:border-main hover:bg-surface-elevation-1': visitor.profile.networkingAccepted }"
    :data-testid="`visitor-card-${visitor.id}`"
    @click="openModal"
  >
    <div class="flex grow flex-col items-center text-center transition-transform duration-(--motion-duration-fast)">
      <img
        :src="visitor.profile.profileImage"
        alt="Visitor profile image"
        class="-mt-3.75 size-30 overflow-hidden rounded-full border-4 border-inverse object-cover shadow transition-all"
        :class="{ 'group-hover:scale-110': visitor.profile.networkingAccepted }"
      />

      <div v-if="visitor.profile.networkingAccepted" class="mt-4 flex items-center gap-2 text-body-m-bold">
        {{ visitor.profile.name }}
        <MdiSvg :path="mdiArrowTopRight" :size="16" />
      </div>
      <div v-else class="mt-6"></div>

      <div v-if="visitor.profile.position" class="mt-1 text-body-s text-subtlest">{{ visitor.profile.position }}</div>
      <div v-if="visitor.profile.companyName" class="text-body-s-bold text-subtlest">{{ visitor.profile.companyName }}</div>
    </div>

    <div class="mt-3 flex shrink-0 grow-0 justify-center">
      <DropDownSelect v-if="visitor.profile.networkingAccepted">
        <template #activator="{ open }">
          <button
            :class="visitorStatusColorClass(visitor.status)"
            class="visitor-status-card flex items-center gap-1 px-2 py-1"
            data-testid="visitor-card-status"
            @click.stop.prevent="open"
          >
            {{ $t(`VisitorProfiles.profileCard.status.${visitor.status}`) }}
            <MdiSvg :path="mdiChevronDown" :size="24" />
          </button>
        </template>

        <template #default="{ close }">
          <ul class="flex cursor-default flex-col gap-4 px-3 py-2" data-testid="visitor-card-options" @click.stop>
            <li v-for="option in options" :key="`filter_${option.value}`" class="flex items-center gap-4">
              <button
                :class="visitorStatusColorClass(option.value)"
                class="visitor-status-card px-2 py-1 text-nowrap"
                :data-testid="`visitor-card-option-${option.value}`"
                @click.stop.prevent="(handleCheckboxChange(option.value), close())"
              >
                {{ option.text }}
              </button>
              <MdiSvg v-if="visitor.status === option.value" :path="mdiCheck" :size="18" class="text-subtlest" />
            </li>
          </ul>
        </template>
      </DropDownSelect>
      <span v-else class="visitor-status visitor-status-card-withdrawn px-2 py-1" data-testid="visitor-card-withdrawn">
        {{ $t('VisitorProfiles.profileCard.status.withdrawn') }}
      </span>
    </div>
  </div>

  <VisitorProfileModal :show="popupToggle" :visitor="visitor" @close="closeModal" />
</template>

<script setup lang="ts">
import { mdiChevronDown, mdiCheck, mdiArrowTopRight } from '@mdi/js'
import { MdiSvg } from '@ramp106/omrjs-core-ui'
import { ref, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { VisitorProfileStatusEnum, useUpdateVisitorProfileStatusMutation } from '@/gql/myomr'
import { showNotification } from '@/helpers/notificationHelper'
import type { EnsureAllKeys } from '@/types'
import DropDownSelect from '@/ui/DropDownSelect.vue'

import { ROOT_EVENT_SLUG } from './config'
import type { CompanyVisitorProfile } from './types'
import VisitorProfileModal from './VisitorProfileModal.vue'

const STATUS_ORDER: EnsureAllKeys<VisitorProfileStatusEnum> = [
  VisitorProfileStatusEnum.Unprocessed,
  VisitorProfileStatusEnum.Interesting,
  VisitorProfileStatusEnum.Contacted,
  VisitorProfileStatusEnum.NotRelevant,
]

const props = defineProps<{
  visitor: CompanyVisitorProfile
}>()

const { t } = useI18n()

const { mutate: updateVisitorProfileStatus, onError, onDone } = useUpdateVisitorProfileStatusMutation()

const popupToggle = ref(false)

const options = computed(() => {
  return STATUS_ORDER.map((visitorStatus) => ({
    value: visitorStatus as VisitorProfileStatusEnum,
    text: t(`VisitorProfiles.profileCard.status.${visitorStatus}`),
  }))
})

function visitorStatusColorClass(status: VisitorProfileStatusEnum) {
  return `visitor-status-${status.toLowerCase()}`
}

async function handleCheckboxChange(value: VisitorProfileStatusEnum) {
  const result = await updateVisitorProfileStatus({
    id: props.visitor.id,
    status: value,
    eventSlug: ROOT_EVENT_SLUG,
  })

  if (result?.data?.updateVisitorProfileStatus?.errors?.length) {
    result.data.updateVisitorProfileStatus.errors.forEach((error) => {
      showError(error)
    })
  }
}

onError((error) => {
  showError(error.message)
})

onDone(() => {
  showNotification(t('VisitorProfiles.profileCard.statusChanged'), 'success')
})

function showError(error: string) {
  if (error) showNotification(error, 'error')
}

function openModal() {
  if (!props.visitor.profile.networkingAccepted) return
  popupToggle.value = true
}

function closeModal() {
  popupToggle.value = false
}

onUnmounted(() => {
  if (popupToggle.value) {
    document.body.classList.remove('body-scroll-lock')
  }
})
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

@utility visitor-status {
  @apply flex items-center gap-1 rounded-sm border text-[14px] leading-[1.5] font-bold not-italic;
}

.visitor-status-card.visitor-status-unprocessed {
  @apply visitor-status;
  @apply border-purple-600/20 bg-primary-subtler text-primary;
}

.visitor-status-card.visitor-status-interesting {
  @apply visitor-status;
  @apply border-warning-subtle bg-warning-subtle text-warning;
}

.visitor-status-card.visitor-status-contacted {
  @apply visitor-status;
  @apply border-success-subtle bg-success-subtle text-success;
}

.visitor-status-card.visitor-status-not_relevant {
  @apply visitor-status;
  @apply border-subtle bg-surface-elevation-2 text-subtle;
}

.visitor-status-card-withdrawn {
  @apply visitor-status;
  @apply border-main bg-surface-elevation-2 text-subtlest;
}
</style>
